import React, { useState } from 'react';
import { arrayUnion, Timestamp, updateDoc } from 'firebase/firestore';
import logger from '../../utils/logger';
import { gradeLabel, subjectLabel } from '../../utils/catalogue';
import { NAMING_HINT, generateTitle, normaliseTitle } from '../../utils/resourceNaming';

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:outline-none';

/**
 * REQ-04 — moderators (in scope) and administrators rename a resource during
 * review. Only the display title on the record changes; the stored file is
 * untouched. Every rename is recorded with who made it and when.
 */
const RenameDialog = ({ pdf, actor, actorUid, onClose, onDone }) => {
  const [theme, setTheme] = useState(pdf.theme || '');
  const [title, setTitle] = useState(pdf.title || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const suggested = generateTitle({ gradeName: gradeLabel(pdf.grade), subjectName: subjectLabel(pdf.subject), theme });
  const history = [...(pdf.renameHistory || [])].sort((a, b) => (b.at?.toMillis?.() || 0) - (a.at?.toMillis?.() || 0));

  const save = async (e) => {
    e.preventDefault();
    const clean = normaliseTitle(title);
    if (!clean) {
      setError('A document name is required.');
      return;
    }
    if (clean === pdf.title && theme.trim() === (pdf.theme || '')) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const changes = { theme: theme.trim() };
      if (clean !== pdf.title) {
        Object.assign(changes, {
          title: clean,
          titleLower: clean.toLowerCase(),
          renamedAt: Timestamp.now(),
          renamedBy: actor,
          renameHistory: arrayUnion({ from: pdf.title || '', to: clean, by: actor, byUid: actorUid || null, at: Timestamp.now() }),
        });
      }
      await updateDoc(pdf.ref, changes);
      onDone?.(clean !== pdf.title ? 'Resource renamed.' : 'Theme updated.');
      onClose();
    } catch (err) {
      logger.error('Rename failed:', err);
      setError(err?.code === 'permission-denied' ? 'This resource is outside your moderation scope.' : 'Could not rename. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={() => !saving && onClose()} />
      <form onSubmit={save} role="dialog" aria-modal="true" className="relative z-[1001] w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Rename resource</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {subjectLabel(pdf.subject)} · {gradeLabel(pdf.grade)}. The stored file is not changed.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Theme</label>
            <input value={theme} onChange={(e) => setTheme(e.target.value)} className={inputClass} placeholder="e.g. Fractions" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">Document name</label>
              {suggested && suggested !== title && (
                <button type="button" onClick={() => setTitle(suggested)} className="text-xs font-medium text-brand-600 hover:text-brand-700">
                  Use “{suggested}”
                </button>
              )}
            </div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
            <p className="text-xs text-gray-400 mt-1.5">Naming convention: {NAMING_HINT}.</p>
          </div>

          {history.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1.5">Rename history</p>
              <ul className="max-h-40 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50 text-xs">
                {history.map((h, i) => (
                  <li key={i} className="px-3 py-2">
                    <p className="text-gray-700 break-words">
                      <span className="line-through text-gray-400">{h.from || '(untitled)'}</span> → {h.to}
                    </p>
                    <p className="text-gray-400 mt-0.5">{h.by} · {h.at?.toDate?.().toLocaleString()}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:bg-gray-400">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RenameDialog;
